import React from 'react'
import PropTypes from 'prop-types'

const Readme = props => {
    return (
        <div className='readme_body'>
            <main>
                <h1 id="inventory-management-system-–-react-frontend">Inventory Management System – React Frontend</h1>
<p>A simple, responsive web app for inventory management built with <strong>React</strong>, <strong>TypeScript</strong>, and <strong>TailwindCSS</strong>. This system enables efficient product management through a simple interface, role-based access control, and is built using modern frontend technologies.</p>
<p><img src="https://github.com/tunde262/Inventory_Management_System_React_Typescript/blob/main/assets/thumbnail.png?raw=true" alt="thumbnail" /></p>
<hr />
<h2 id="project-description">Project Description</h2>
<p>This project serves as the frontend for a basic inventory management system. It allows e-commerce / brick-and-mortar owners to <strong>manage products</strong> and <strong>sign in / register securely</strong> through a very simple UI. The app leverages a Java-Spring Backend API that depends on a PostgrSQL database. The code architectural practices, include <strong>TypeScript typing</strong>, <strong>modular components</strong>, and <strong>state-driven UI rendering</strong>.</p>
<hr />
<h2 id="tech-stack">Tech Stack</h2>
<table>
<thead>
<tr>
<th id="category">Category</th>
<th id="technology">Technology</th>
</tr>
</thead>
<tbody>
<tr>
<td>Framework</td>
<td>React</td>
</tr>
<tr>
<td>Language</td>
<td>TypeScript</td>
</tr>
<tr>
<td>Styling</td>
<td>TailwindCSS, ShadCN UI</td>
</tr>
<tr>
<td>Routing</td>
<td>TanStack Router</td>
</tr>
<tr>
<td>State Management</td>
<td>TanStack Query</td>
</tr>
<tr>
<td>HTTP Client</td>
<td>Axios</td>
</tr>
<tr>
<td>Build Tool</td>
<td>Vite</td>
</tr>
<tr>
<td>Version Control</td>
<td>Git</td>
</tr>
<tr>
<td>Testing (Backend Only)</td>
<td>JUnit, Mockito</td>
</tr>
</tbody>
</table>
<hr />
<h2 id="features">Features</h2>
<ul>
<li>JWT or Session-based Authentication</li>
<li>Authentication State Management with refresh/expiration handling</li>
<li>Role-Based Access Control (RBAC)</li>
<li>CRUD Operations on Products</li>
<li>Product Search, Filtering, Sorting</li>
<li>Image Uploads &amp; Bulk Product Creation</li>
<li>Soft Delete &amp; Bulk Deletion Support</li>
<li>Fully Typed React Components with Hooks</li>
<li>Responsive UI using TailwindCSS + ShadCN</li>
<li>Loading and Error States with Feedback</li>
<li>Optimistic UI Updates</li>
<li>Pagination, Filtering, and Data Display</li>
<li>Axios-based API Integration</li>
<li>Backend Test Coverage with Jest</li>
</ul>
<hr />
<h2 id="user-stories--acceptance-criteria">User Stories &amp; Acceptance Criteria</h2>
<h3 id="authentication--authorization">Authentication &amp; Authorization</h3>
<p><strong>User Login</strong></p>
<ul>
<li>Email/password login form using ShadCN components</li>
<li>Secure JWT (in-memory) or Session-based (cookie) implementation</li>
<li>Error handling and protected routes</li>
</ul>
<p><strong>Auth State Management</strong></p>
<ul>
<li>JWT Tokens</li>
<li>Redirects on invalid token</li>
</ul>
<p><strong>Role-Based Access Control</strong></p>
<ul>
<li>Show/hide UI features based on role</li>
<li>Protect routes via TanStack Router</li>
<li>Graceful handling of unauthorized access</li>
</ul>
<hr />
<h3 id="product-management">Product Management</h3>
<p><strong>View Products</strong></p>
<ul>
<li>Fetch via TanStack Query</li>
<li>Show loaders, errors, and paginated product list</li>
<li>Filtering by category, sorting</li>
</ul>
<p><strong>Search Products</strong></p>
<ul>
<li>Real-time search with debounce</li>
<li>Filter by name, description</li>
<li>Show "No results" message</li>
</ul>
<p><strong>Add Product</strong></p>
<ul>
<li>Validated form (with image upload and bulk creation)</li>
<li>Success/error notifications</li>
<li>Form reset on success</li>
</ul>
<p><strong>Edit Product</strong></p>
<ul>
<li>Pre-filled form for editing</li>
<li>Success/error feedback</li>
</ul>
<p><strong>Delete Product</strong></p>
<ul>
<li>Confirmation modal before delete</li>
<li>Bulk delete support</li>
</ul>
<hr />
<h2 id="technical-highlights">Technical Highlights</h2>
<h3 id="react-implementation">React Implementation</h3>
<ul>
<li>Component-based architecture</li>
<li>Custom hooks for API/data logic</li>
<li>Strong TypeScript usage</li>
</ul>
<h3 id="styling--ux">Styling &amp; UX</h3>
<ul>
<li>Fully responsive using TailwindCSS</li>
<li>Accessible, interactive components via ShadCN</li>
<li>Consistent design language</li>
</ul>
<h3 id="api-integration">API Integration</h3>
<ul>
<li>Axios with global interceptors</li>
<li>Full error/loading management</li>
<li>Query caching and real-time feedback</li>
</ul>
<hr />
<h2 id="getting-started">Getting Started</h2>
<h1 id="clone-the-repo">Clone the repo</h1>
<pre><code class="hljs bash language-bash">git <span class="hljs-built_in">clone</span> https://github.com/your-username/inventory-frontend.git
<span class="hljs-built_in">cd</span> inventory-frontend</code></pre>
<h1 id="install-dependencies">Install dependencies</h1>
<pre><code class="hljs bash language-bash">npm install</code></pre>
<h1 id="run-the-development-server">Run the development server</h1>
<pre><code class="hljs bash language-bash">npm run dev</code></pre>
            </main>
        </div>
  )
}

Readme.propTypes = {}

export default Readme;
